import { Children, cloneElement, isValidElement, useId, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Portal } from '../../core/Portal/Portal.js';
import { computePosition, type Placement } from '../floating-positioner.js';
import { useReducedMotionMultiplier } from '../../motion/hooks/useReducedMotionMultiplier.js';
import './Tooltip.css';

export interface TooltipProps {
  label: ReactNode;
  position?: Placement;
  offset?: number;
  /** Open delay in ms. */
  openDelay?: number;
  /** Close delay in ms. */
  closeDelay?: number;
  withArrow?: boolean;
  /** Force-render content even when not hovered (debugging). */
  opened?: boolean;
  children: ReactElement;
  /** Color tint for the tooltip background. */
  color?: string;
  /** Max width before wrapping. */
  maxWidth?: number;
  className?: string;
}

/** Lightweight tooltip on hover + focus. */
export function Tooltip({
  label,
  position = 'top',
  offset = 6,
  openDelay = 100,
  closeDelay = 0,
  withArrow,
  opened: openedProp,
  children,
  color,
  maxWidth = 240,
  className
}: TooltipProps): ReactNode {
  const [hovered, setHovered] = useState(false);
  const opened = openedProp ?? hovered;
  const anchorRef = useRef<HTMLElement | null>(null);
  const floatingRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const multiplier = useReducedMotionMultiplier();
  const tipId = useId();

  /*
   * Held in state, not only in a ref: `Portal` renders null until its own
   * effect has run, so the node does not exist on the commit where this opens.
   * Reading a still-null ref here made the effect bail and never run again,
   * leaving the panel parked at its pre-measurement -9999px.
   */
  const [floatingEl, setFloatingEl] = useState<HTMLDivElement | null>(null);
  const setFloatingNode = (node: HTMLDivElement | null) => {
    floatingRef.current = node;
    setFloatingEl(node);
  };

  useLayoutEffect(() => {
    if (!opened || !anchorRef.current || !floatingEl) return;
    const a = anchorRef.current.getBoundingClientRect();
    const f = floatingEl.getBoundingClientRect();
    setCoords(computePosition(a, { width: f.width, height: f.height }, { placement: position, offset }));
  }, [opened, position, offset, label, floatingEl]);

  const show = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    openTimer.current = window.setTimeout(() => setHovered(true), openDelay);
  };
  const hide = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    closeTimer.current = window.setTimeout(() => setHovered(false), closeDelay);
  };

  if (!isValidElement(children)) return children;
  const child = children as ReactElement<{
    ref?: React.Ref<HTMLElement>;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
    'aria-describedby'?: string;
  }>;

  const enhanced = cloneElement(child, {
    ref: (node: HTMLElement | null) => { anchorRef.current = node; },
    onMouseEnter: (e: React.MouseEvent) => { child.props.onMouseEnter?.(e); show(); },
    onMouseLeave: (e: React.MouseEvent) => { child.props.onMouseLeave?.(e); hide(); },
    onFocus: (e: React.FocusEvent) => { child.props.onFocus?.(e); show(); },
    onBlur: (e: React.FocusEvent) => { child.props.onBlur?.(e); hide(); },
    'aria-describedby': opened ? tipId : child.props['aria-describedby']
  });

  return (
    <>
      {enhanced}
      {opened ? (
        <Portal>
          <div
            ref={setFloatingNode}
            id={tipId}
            role="tooltip"
            className={['rr-tooltip', className].filter(Boolean).join(' ')}
            style={{
              position: 'fixed',
              top: coords ? `${coords.y}px` : '-9999px',
              left: coords ? `${coords.x}px` : '-9999px',
              maxWidth,
              background: color,
              zIndex: 'var(--rr-z-tooltip)' as unknown as number,
              animationDuration: multiplier === 0 ? '0ms' : undefined
            }}
          >
            {label}
            {withArrow ? <span className="rr-tooltip__arrow" aria-hidden /> : null}
          </div>
        </Portal>
      ) : null}
    </>
  );
}

Tooltip.Children = Children; // Trivial re-export for compactness
